// Re-exports
export * from "./models";
export * from "./utils";
export * from "./constants";

import _Vue from "vue";
import {
  I18nPlugin,
  I18nPluginOptions,
  I18nMessages,
  I18nFlattenMessages,
  I18nFlattenDictationary,
} from "./models";
import { I18N_ERR } from "./constants";

/**
 * Given a JSON of {key: value} pair when the value is either a text
 * or another JSON, returns the flatten version of the JSON to get
 * a single level of {key: value} pair only
 * @param msgs I18n JSON
 */
export const flattenMessages = (msgs: I18nMessages): I18nFlattenMessages => {
  let flattenMsgs: I18nFlattenMessages = {};

  Object.keys(msgs).map((key) => {
    const val = msgs[key];

    // String found, simply copy it
    if (typeof val === "string") {
      flattenMsgs[key] = val;
    }
    // It is a nested messages set. Flatten recursively
    else {
      const flattenEntries = flattenMessages(val);
      Object.keys(flattenEntries).forEach((flattenKey) => {
        flattenMsgs[`${key}.${flattenKey}`] = flattenEntries[flattenKey];
      });
    }
  });

  return flattenMsgs;
};

/**
 * Ensure that the same keys are present in all languages
 *
 * @param flattenDict
 */
export const isDictionaryValid = (
  flattenDict: I18nFlattenDictationary
): boolean => {
  const languages = Object.keys(flattenDict);

  // scan all languages
  for (const [lang, msgs] of Object.entries(flattenDict)) {
    // Check each key is present in other languages
    for (const msgKey of Object.keys(msgs)) {
      const hasError = languages
        .filter((test) => test !== lang)
        .some((testLang) => !flattenDict[testLang][msgKey]);

      if (hasError) return false;
    }
  }

  return true;
};

/**
 * Given a dictionary, get the message for the appropriate key and languages.
 *
 * TODO: placeholder management
 * @param dict
 */
export const getMessage = (dict: I18nFlattenDictationary) => (
  key: string,
  lang: string,
  args?: any[]
): string => {
  // Fetch dictionary
  const translationDict = dict[lang];

  // Language is found: fetch the request message
  if (translationDict) {
    let msg = translationDict[key];

    // Replace position-based placeholder
    if (msg && args) {
      for (let i = 0; i < args.length; i++) {
        msg = msg.replace(`{${i}}`, args[i]);
      }
    } else {
      // error handling to be defined
    }

    return msg;
  }
  // TODO: proper error handling
  else {
    return `Lang ${lang} not found`;
  }
};

const Plugin: I18nPlugin = {
  // ==========================================================================
  //  Properties
  // ==========================================================================
  installed: false,
  // default value
  getLanguage: () => "en",
  // default value
  flattenDictionary: { en: {} },

  // ==========================================================================
  //  Methods
  // ==========================================================================
  isDictionaryValid,

  /**
   * TODO: handle args
   * @param key
   * @param lang
   * @param args
   */
  getMessage(key, lang, args) {
    return getMessage(this.flattenDictionary)(key, lang, args);
  },
  // ==========================================================================
  //  Vue plugin
  // ==========================================================================
  install(Vue: typeof _Vue, options?: I18nPluginOptions) {
    // Don't install twice
    if (this.installed) {
      return;
    }
    this.installed = true;

    // Options are not optional
    if (!options) {
      throw new Error(I18N_ERR.INSTALL.MISSING_OPTIONS);
    }

    // Forward options to the plugin instance
    this.getLanguage = options.getLanguage || (() => "en");

    // Flatten dictionary
    const flattenDict: I18nFlattenDictationary = {};
    Object.keys(options.dictionary).forEach((lang) => {
      const flatten = flattenMessages(options.dictionary[lang]);
      flattenDict[lang] = flatten;
    });

    // check dictionary
    if (!this.isDictionaryValid(flattenDict)) {
      throw new Error(I18N_ERR.INSTALL.MISMATCH_DICT);
    }

    // Load flatten dictionary
    this.flattenDictionary = flattenDict;

    // default value or custom function
    const defaultGetTranslation = (key: string, ...args: any[]) => {
      return this.getMessage(key, this.getLanguage(), args);
    };
    const effectiveGetTranslation =
      options.getTranslation || defaultGetTranslation;

    // Expose the i18n methods to all Vue instance. If typescript is used, please
    // copy the types.d.ts content to the project root
    Vue.prototype.$__ = effectiveGetTranslation;
    // Global filter definition
    Vue.filter("__", effectiveGetTranslation);
  },
};

export default Plugin;
